import clsx from "clsx";
import { useFormik } from "formik";
import moment from "moment/moment";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import ActiveInactive from "../../components/ActiveInactive";
import DataTableWrapper from "../../components/DataTableWrapper";
import Dropdown from "../../components/Dropdown";
import Loader from "../../components/Loader";
import PaddingContainer from "../../components/PaddingContainer";
import PageHeading from "../../components/PageHeading";
import Popover from "../../components/Popovers";
import SearchInput from "../../components/SearchInput";
import TableValue from "../../components/TableValue";
import UserStats from "../../components/UserStats";
import { CalendarIcon, DownloadPDFIcon, ViewDetailIcon } from "../../constant";
import useDebounce from "../../CustomHooks/useDebounce";
import { statsPeriod } from "../../data";
import { formatPhone, formattedDate } from "../../helpers";
import { findLatestBooking } from "../../lib/bookings";
import { exportUserDetailToPDF } from "../../pdfs/users/userDetail";
import { setPageLink, setPageTitle } from "../../store/pageSlice";
import useApi from "../../useApi";
import classes from "./Users.module.css";
// Add sorting fields enum
const UserSortFields = {
  Name: 1,
  Email: 2,
  TotalBooking: 3,
  LastBookingDate: 4,
  Status: 5,
};
const initialValues = {
  // Add any filters needed
};
let isStatsRender1st = true;
const derivePeriod = (period) => {
  if (period === 1) {
    return "Today";
  }
  if (period === 2) {
    return "This Week";
  }
  if (period === 3) {
    return "This Month";
  }
  return "This Year";
};

export default function Users() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { getUsers, getUserStats } = useApi();
  const [userStats, setUserStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(10);
  const [search, setSearch] = useState("");
  const [sortState, setSortState] = useState({
    field: null,
    order: null,
  });

  const debouncedSearch = useDebounce(search, 600);
  const [period, setPeriod] = useState(statsPeriod[0].value);
  // Initialize form with filters
  const { values, setFieldValue } = useFormik({
    initialValues,
  });

  const getUserStatsApi = async (period, startLoading = false) => {
    try {
      if (startLoading) {
        setLoading(true);
      }
      const response = await getUserStats({
        intervalId: period,
      });
      if (response?.data?.headers?.error === 200) {
        setUserStats(response?.data?.body);
      } else {
        toast.error(response?.data?.headers?.message);
      }
    } catch (error) {
      toast.error(new Error(error?.message));
    }
    if (startLoading) {
      setLoading(false);
    }
  };
  // Set page title
  useEffect(() => {
    dispatch(setPageTitle("Users"));

    dispatch(setPageLink(""));
  }, [dispatch]);

  // Fetch users data
  useEffect(() => {
    if (isStatsRender1st) {
      (async () => {
        Promise.allSettled([getUserStatsApi(period), fetchUsers()]);
      })();
      isStatsRender1st = false;
    } else {
      fetchUsers();
    }
    //
  }, [debouncedSearch, first, rows, sortState]);
  useEffect(() => {
    if (debouncedSearch || first || rows || sortState) {
    }
  }, [debouncedSearch, first, rows, sortState]);

  const fetchUsers = async () => {
    try {
      setLoading(true);

      const params = {
        search: debouncedSearch,
        pageNumber: first / rows + 1,
        pageSize: rows,
        ...(sortState.field && { userAdminSortId: sortState.field }),
        isDesc: sortState.order === -1,
        showInactive: true,
        showUpcoming: true,
      };

      const response = await getUsers(params);

      if (response?.data?.headers?.error === 200) {
        setUsers(response?.data?.body?.items || []);
        setTotalCount(response?.data?.body?.totalItems || 0);
      } else {
        toast.error(
          response?.data?.headers?.message || "Failed to fetch users"
        );
      }
    } catch (error) {
      toast.error(new Error(error?.message));
    }
    setLoading(false);
  };

  // Enhanced pagination handler to handle rows per page change
  const handlePageChange = (event) => {
    console.log("Page change event:", event);
    setFirst(event.first);
    // If rows per page has changed
    if (event.rows !== rows) {
      setRows(event.rows);
    }
  };

  // Handle sorting
  const handleSort = (event) => {
    setSortState({
      field: event.sortField,
      order: event.sortOrder,
    });
  };

  // Define table columns
  const columns = [
    {
      field: "userFullName",
      header: "Name",
      sortable: true,
      sortField: UserSortFields.Name,
      body: (rowData) => (
        <TableValue
          top={rowData?.userFullName || "--"}
          down={
            (rowData?.cellNumber &&
              formatPhone(rowData?.cellNumber?.replace(/\s+/g, ""))) ||
            "--"
          }
        />
      ),
    },
    {
      field: "userEmail",
      header: "Email Address",
      sortable: true,
      sortField: UserSortFields.Email,
      body: (rowData) => <TableValue top={rowData?.email || "--"} />,
    },
    {
      field: "totalBookings",
      header: "Total Bookings",
      sortable: true,
      sortField: UserSortFields.TotalBooking,
      body: (rowData) => <TableValue top={rowData?.bookings?.length || 0} />,
    },
    {
      field: "lastBookingDate",
      header: "Last Booking Date",
      sortable: true,
      sortField: UserSortFields.LastBookingDate,
      body: (rowData) => {
        return (
          <TableValue
            top={formattedDate(
              findLatestBooking(rowData?.bookings)?.bookingDate
            )}
            down={moment(rowData?.lastBookingDate).format("hh:mm A")}
          />
        );
      },
    },
    {
      field: "status",
      header: "Booking Status",
      sortable: false,
      sortField: UserSortFields.Status,
      body: (rowData) => {
        let menuItems = [];

        return <ActiveInactive active={rowData?.active} />;
      },
    },
    {
      field: "actions",
      header: "Actions",
      body: (rowData) => {
        const items = [
          {
            label: "View Detail",
            icon: <ViewDetailIcon className={classes.menuIcon} />,
            command: () => {
              navigate(`/users/${rowData?.userDetailId}`, {
                state: { userData: rowData }
              });
            },
          },

          {
            label: "Download PDF",
            icon: <DownloadPDFIcon className={classes.menuIcon} />,
            command: () => {
              exportUserDetailToPDF(rowData);
            },
          },
        ];

        return <Popover items={items} />;
      },
    },
  ];

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <PaddingContainer>
          <section className={classes.titleContainer}>
            <PageHeading label="Overall Summary" />
            <Dropdown
              className={clsx(classes.filterAction, classes.bgWhite)}
              placeholder={"Export"}
              icon={<CalendarIcon />}
              options={statsPeriod}
              value={period}
              onChange={(e) => {
                getUserStatsApi(e, true);
                setPeriod(e);
              }}
            />
          </section>
          <UserStats data={userStats} period={derivePeriod(period)} />

          <section className={clsx(classes.titleContainer, classes.mt30)}>
            <PageHeading label="User List" />
            <div className={classes.searchContainer}>
              <SearchInput
                placeholder={"Search"}
                onChange={setSearch}
                value={search}
              />
            </div>
          </section>

          <DataTableWrapper
            search={debouncedSearch}
            data={users}
            columns={columns}
            loading={loading}
            totalRecords={totalCount}
            onPageChange={handlePageChange}
            first={first}
            rows={rows}
            onSort={handleSort}
            sortField={sortState.field}
            sortOrder={sortState.order}
            paginator={true}
            rowsPerPageOptions={[5, 10, 20, 50]}
          />
        </PaddingContainer>
      )}
    </>
  );
}
